import { t } from '@lingui/core/macro';
import { CarAxleType, TireQuoteItemRecordCustomer, TireQuoteItemTireRecordCustomer } from '@navio/nsdk';

/** One quantity line of a tire option: an axle on a staggered option, all tires otherwise. */
type TireOptionQuantityRow = {
  label: string;
  tire: TireQuoteItemTireRecordCustomer;
  quantity: number;
};

export type TireOptionQuantityRows = [TireOptionQuantityRow, ...TireOptionQuantityRow[]];

/** The option's quantity lines; null for a staggered option without both a front and a rear tire. */
export const getTireOptionQuantityRows = (quoteItem: TireQuoteItemRecordCustomer): TireOptionQuantityRows | null => {
  if (quoteItem.isStaggered) {
    const front = quoteItem.quoteItemTires.find((tire) => tire.axle?.type === CarAxleType.FRONT);
    const rear = quoteItem.quoteItemTires.find((tire) => tire.axle?.type === CarAxleType.REAR);
    if (!front || !rear) {
      return null;
    }
    return [
      { label: t`Front tires`, tire: front, quantity: getTireQuantity(front) },
      { label: t`Rear tires`, tire: rear, quantity: getTireQuantity(rear) }
    ];
  }

  const [first] = quoteItem.quoteItemTires;
  if (!first) {
    return null;
  }
  const quantity = quoteItem.quoteItemTires.reduce((sum, tire) => sum + getTireQuantity(tire), 0);
  return [{ label: t`Tires`, tire: first, quantity }];
};

/** The quote item with every tire's `quantitySelected` set, as the totals, the next steps and the order read it. */
export const withTireOptionQuantities = (quoteItem: TireQuoteItemRecordCustomer): TireQuoteItemRecordCustomer => ({
  ...quoteItem,
  quoteItemTires: quoteItem.quoteItemTires.map((tire) => ({ ...tire, quantitySelected: getTireQuantity(tire) }))
});

// The quantity is the offer's (set by staff, else the car's default); the customer cannot change it.
const getTireQuantity = (tire: TireQuoteItemTireRecordCustomer) => tire.quantitySelected || tire.quantityDefault || 0;
